import React, { useState, useEffect } from 'react';
import { Search, MapPin, Star, Filter, X, Wifi, Coffee, Car, Bath, Dumbbell } from 'lucide-react';
import hotelData from '../../data/hotels.json';
import { Link, useLocation } from 'react-router-dom';

const FilterHotels = () => {

  // Handle view details navigation
  // const handleViewDetails = (hotel) => {
    
  //   try {
  //     navigate(`/hotel/${hotel.id}`, { 
  //       state: { 
  //         hotel: hotel, 
  //         searchParams: filters 
  //       } 
  //     });
  //   } catch (error) {
  //     console.error("Navigation error:", error);
  //     // Fallback to traditional navigation method
  //     window.location.href = `/hotel/${hotel.id}`;
  //   }
  // };
  
  const location = useLocation();
  // const navigate = useNavigate();
  
  // Get data from route state if available
  const initialHotels = location.state?.filteredHotels || hotelData.hotels || [];
  const initialSearchParams = location.state?.searchParams || {};
  
  const [hotels] = useState(initialHotels);
  const [displayHotels, setDisplayHotels] = useState(initialHotels);
  const [showFilters, setShowFilters] = useState(false);
  const [sortOption, setSortOption] = useState('recommended');
  const [loading, setLoading] = useState(false);
  
  // Set up filters with initial values from search if available
  const [filters, setFilters] = useState({
    priceRange: [0, 1000],
    starRating: [],
    amenities: [],
    propertyType: '',
    halalStatus: [],
    search: initialSearchParams.location || ''
  });

  // Price ranges for filter
  const priceRanges = [
    { label: '$0 - $100', value: [0, 100] },
    { label: '$100 - $300', value: [100, 300] },
    { label: '$300 - $500', value: [300, 500] },
    { label: '$500+', value: [500, 5000] }
  ];

  // Amenities with icons
  const amenities = [
    { name: 'Free WiFi', icon: <Wifi size={18} /> },
    { name: 'Pool', icon: <Bath size={18} /> },
    { name: 'Parking', icon: <Car size={18} /> },
    { name: 'Fitness Center', icon: <Dumbbell size={18} /> },
    { name: 'Restaurant', icon: <Coffee size={18} /> }
  ];

  // Property types
  const propertyTypes = ['Hotel', 'Resort', 'Villa', 'Apartment'];

  // Halal status options
  const halalStatuses = [
    'Halal Certified',
    'Muslim Friendly',
    'Prayer Facilities Available'
  ];

  // Apply filters and sorting to hotels
  useEffect(() => {
    setLoading(true);
    
    // Filter the hotels based on all criteria
    let filteredResults = [...hotels];
    
    // Filter by price range
    filteredResults = filteredResults.filter(hotel => 
      hotel.price >= filters.priceRange[0] && hotel.price <= filters.priceRange[1]
    );
    
    // Filter by star rating
    if (filters.starRating.length > 0) {
      filteredResults = filteredResults.filter(hotel => 
        filters.starRating.includes(hotel.stars)
      );
    }
    
    // Filter by amenities
    if (filters.amenities.length > 0) {
      filteredResults = filteredResults.filter(hotel => 
        filters.amenities.every(amenity => hotel.amenities.includes(amenity))
      );
    }
    
    // Filter by property type
    if (filters.propertyType) {
      filteredResults = filteredResults.filter(hotel => 
        hotel.type === filters.propertyType
      );
    }
    
    // Filter by halal status
    if (filters.halalStatus.length > 0) {
      filteredResults = filteredResults.filter(hotel => 
        filters.halalStatus.includes(hotel.halal_status)
      );
    }
    
    // Filter by search term
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filteredResults = filteredResults.filter(hotel => 
        hotel.name.toLowerCase().includes(searchTerm) ||
        hotel.location.city.toLowerCase().includes(searchTerm) ||
        hotel.location.country.toLowerCase().includes(searchTerm) ||
        hotel.description?.toLowerCase().includes(searchTerm)
      );
    }
    
    // Sort the results
    switch (sortOption) {
      case 'price-low-high':
        filteredResults.sort((a, b) => a.price - b.price);
        break;
      case 'price-high-low':
        filteredResults.sort((a, b) => b.price - a.price);
        break;
      case 'rating-highest':
        filteredResults.sort((a, b) => {
          if (b.stars !== a.stars) return b.stars - a.stars;
          return b.reviews.score - a.reviews.score;
        });
        break;
      default:
        // Default 'recommended' sort - can implement custom logic here
        // For now, sort by a combination of rating and price
        filteredResults.sort((a, b) => {
          const scoreA = a.stars * 20 + (100 - a.price / 10);
          const scoreB = b.stars * 20 + (100 - b.price / 10);
          return scoreB - scoreA;
        });
    }
    
    // Update the display
    setDisplayHotels(filteredResults);
    setLoading(false);
  }, [filters, sortOption, hotels]);
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setFilters(prev => ({ ...prev, search: e.target.value }));
  };
  
  // Handle search submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    // The useEffect will handle the actual filtering
  };

  // Handle filter changes
  const handleFilterChange = (type, value) => {
    setFilters(prev => ({ ...prev, [type]: value }));
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      priceRange: [0, 1000],
      starRating: [],
      amenities: [],
      propertyType: '',
      halalStatus: [],
      search: ''
    });
    setSortOption('recommended');
  };
  
  // Handle sort change
  const handleSortChange = (e) => {
    setSortOption(e.target.value);
  };

  return (
    <div className="container-fluid py-4">
      {/* Search bar */}
      <div className="row mb-4">
        <div className="col-lg-12">
          <form onSubmit={handleSearchSubmit} className="position-relative">
            <div className="input-group">
              <span className="input-group-text bg-white">
                <Search size={20} />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search hotels, locations, amenities..."
                value={filters.search}
                onChange={handleSearchChange}
              />
              {filters.search && (
                <button 
                  type="button" 
                  className="btn btn-outline-secondary"
                  onClick={() => setFilters(prev => ({ ...prev, search: '' }))}
                >
                  <X size={20} />
                </button>
              )}
              <button type="submit" className="btn btn-primary">
                Search
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="row">
        {/* Mobile Filter Button */}
        <div className="d-lg-none col-12 mb-3">
          <button 
            className="btn btn-outline-primary w-100"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={18} className="me-2" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
        </div>

        {/* Filters Sidebar */}
        <div className={`col-lg-3 mb-4 ${showFilters ? 'd-block' : 'd-none d-lg-block'}`}>
          <div className="card shadow-sm">
            <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Filters</h5>
              <button 
                className="btn btn-link text-decoration-none p-0"
                onClick={resetFilters}
              >
                Reset all
              </button>
            </div>
            
            <div className="card-body">
              {/* Price Range */}
              <div className="mb-4">
                <h6 className="mb-3">Price Range</h6>
                {priceRanges.map((range, index) => (
                  <div key={index} className="form-check mb-2">
                    <input
                      type="radio"
                      className="form-check-input"
                      name="priceRange"
                      id={`price-${index}`}
                      checked={filters.priceRange[0] === range.value[0] && filters.priceRange[1] === range.value[1]}
                      onChange={() => handleFilterChange('priceRange', range.value)}
                    />
                    <label className="form-check-label" htmlFor={`price-${index}`}>
                      {range.label}
                    </label>
                  </div>
                ))}
              </div>

              {/* Star Rating */}
              <div className="mb-4">
                <h6 className="mb-3">Star Rating</h6>
                {[5, 4, 3, 2, 1].map(stars => (
                  <div key={stars} className="form-check mb-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`star-${stars}`}
                      checked={filters.starRating.includes(stars)}
                      onChange={() => {
                        const newStars = filters.starRating.includes(stars)
                          ? filters.starRating.filter(s => s !== stars)
                          : [...filters.starRating, stars];
                        handleFilterChange('starRating', newStars);
                      }}
                    />
                    <label className="form-check-label" htmlFor={`star-${stars}`}>
                      <div className="text-warning d-inline-flex">
                        {[...Array(stars)].map((_, i) => (
                          <Star key={i} size={16} fill="currentColor" />
                        ))}
                      </div>
                    </label>
                  </div>
                ))}
              </div>

              {/* Amenities */}
              <div className="mb-4">
                <h6 className="mb-3">Amenities</h6>
                {amenities.map((amenity, index) => (
                  <div key={index} className="form-check mb-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`amenity-${index}`}
                      checked={filters.amenities.includes(amenity.name)}
                      onChange={() => {
                        const newAmenities = filters.amenities.includes(amenity.name)
                          ? filters.amenities.filter(a => a !== amenity.name)
                          : [...filters.amenities, amenity.name];
                        handleFilterChange('amenities', newAmenities);
                      }}
                    />
                    <label className="form-check-label d-flex align-items-center" htmlFor={`amenity-${index}`}>
                      {amenity.icon}
                      <span className="ms-2">{amenity.name}</span>
                    </label>
                  </div>
                ))}
              </div>

              {/* Property Type */}
              <div className="mb-4">
                <h6 className="mb-3">Property Type</h6>
                <select 
                  className="form-select"
                  value={filters.propertyType}
                  onChange={(e) => handleFilterChange('propertyType', e.target.value)}
                >
                  <option value="">All Types</option>
                  {propertyTypes.map((type, index) => (
                    <option key={index} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Halal Status */}
              <div className="mb-4">
                <h6 className="mb-3">Halal Status</h6>
                {halalStatuses.map((status, index) => (
                  <div key={index} className="form-check mb-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`halal-${index}`}
                      checked={filters.halalStatus.includes(status)}
                      onChange={() => {
                        const newStatus = filters.halalStatus.includes(status)
                          ? filters.halalStatus.filter(s => s !== status)
                          : [...filters.halalStatus, status];
                        handleFilterChange('halalStatus', newStatus);
                      }}
                    />
                    <label className="form-check-label" htmlFor={`halal-${index}`}>
                      {status}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Hotel Listings */}
        <div className="col-lg-9">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h4 className="mb-0">Available Hotels</h4>
            <div className="d-flex align-items-center">
              <span className="text-muted me-3">{displayHotels.length} properties found</span>
              <select 
                className="form-select w-auto"
                value={sortOption}
                onChange={handleSortChange}
              >
                <option value="recommended">Sort by: Recommended</option>
                <option value="price-low-high">Price: Low to High</option>
                <option value="price-high-low">Price: High to Low</option>
                <option value="rating-highest">Rating: Highest First</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Finding the best hotels for you...</p>
            </div>
          ) : (
            <>
              <div className="row g-4">
                {displayHotels.map(hotel => (
                  <div key={hotel.id} className="col-md-6 col-lg-4">
                    <div className="card h-100 shadow-sm hover-shadow">
                      <div className="position-relative">
                        <img 
                          src={hotel.image }
                          alt={hotel.name}
                          className="card-img-top"
                          style={{ height: '200px', objectFit: 'cover' }}
                          onError={(e) => {
                            e.target.src = "";
                          }}
                        />
                        {hotel.discount && (
                          <div className="position-absolute top-0 end-0 m-3 badge bg-danger">
                            {hotel.discount}
                          </div>
                        )}
                        {hotel.halal_status && (
                          <div className="position-absolute bottom-0 start-0 m-3 badge bg-success">
                            {hotel.halal_status}
                          </div>
                        )}
                      </div>
                      <div className="card-body">
                        <h5 className="card-title">{hotel.name}</h5>
                        <div className="d-flex align-items-center mb-2">
                          <MapPin size={16} className="text-muted me-1" />
                          <small className="text-muted">
                            {hotel.location.city}, {hotel.location.country}
                          </small>
                        </div>
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <div className="text-warning">
                            {[...Array(hotel.stars)].map((_, i) => (
                              <Star key={i} size={16} fill="currentColor" />
                            ))}
                          </div>
                          <small className="text-muted">
                            ({hotel.reviews.count} reviews)
                          </small>
                        </div>
                        <div className="mb-3">
                          {hotel.amenities.slice(0, 3).map((amenity, index) => (
                            <span key={index} className="badge bg-light text-dark me-1 mb-1">
                              {amenity}
                            </span>
                          ))}
                          {hotel.amenities.length > 3 && (
                            <span className="badge bg-secondary">
                              +{hotel.amenities.length - 3}
                            </span>
                          )}
                          {hotel.amenities.includes('Restaurant') && (
                            <Link 
                              to={`/restaurantdetails/${hotel.id}`} 
                              state={{ hotelId: hotel.id, hotelName: hotel.name }}
                              className="d-block mt-2 text-decoration-none"
                            >
                              <Coffee size={16} className="text-success me-1" />
                              <span className="text-success">View Restaurant</span>
                            </Link>
                          )}
                        </div>
                      </div>
                      <div className="card-footer bg-white border-top">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <small className="text-muted d-block">Per night</small>
                            <span className="h5 mb-0">${hotel.price}</span>
                          </div>
                          <Link 
                              to={`/hotel/${hotel.id}`}
                              state={{ hotel: hotel, searchParams: filters }}
                              className="btn btn-primary"
                            >
                            View Details
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {displayHotels.length === 0 && (
                <div className="text-center py-5">
                  <Search size={48} className="text-muted mb-3" />
                  <h3>No hotels found</h3>
                  <p className="text-muted">Try adjusting your filters for more results</p>
                  <button className="btn btn-primary" onClick={resetFilters}>
                    Reset Filters
                  </button>
                </div>
              )}
            </>
          )}
          
          {/* Pagination - Add if needed */}
         
        </div>
      </div>
    </div>
  );
};

export default FilterHotels;